import { css } from "@linaria/core";
import React, { useMemo } from "react";
import { color, fonts } from "@ui";
import t from "../../../../../../../../../../i18n";
import type { FaultEvent } from "../../../../../../../../../../api/useFaultEvents";
import { useSetups, type Setup } from "../../../../../../../../../../api/useSetups";
import { comparatorBy, stringComparator, withNull, withOrder } from "diagnostics-lib/comparator";
import { spacings } from "@ui/Theme";
import TooltipV2 from "@ui/TooltipV2";
import { Tag } from "@ui/Tag";
import { StarLine } from "@ui/IconV2/react";

interface FailureInfos {
  failureName: string;
  name: string | null;
  description: string | null;
  degradationType: string | null;
  category: string | null;
  system: string | null;
  equipment: string[];
  suspectedElements: string[];
  equipmentType: string | null;
  isPromoted: boolean;
}

const SidePanelDetails = ({
  projectId,
  faultEvent,
}: {
  projectId: string;
  faultEvent: FaultEvent;
}) => {
  const setups = useSetups(projectId).sort(
    comparatorBy("startDate", withNull(withOrder(stringComparator, "asc"), "first")),
  );
  const setupsByStartDate = useMemo(
    () =>
      setups.sort(comparatorBy("startDate", withNull(withOrder(stringComparator, "asc"), "first"))),
    [setups],
  );

  const setupFaultEvent = React.useMemo(
    () =>
      setupsByStartDate
        .filter(
          setup =>
            setup.startDate === null || new Date(setup.startDate) <= new Date(faultEvent.endsAt),
        )
        .pop() || null,
    [setupsByStartDate, faultEvent],
  );

  const failuresInfos = initializeFailureInfos(faultEvent, setupFaultEvent);

  return (
    <div className={sidePanelDetailsContainer}>
      {failuresInfos.map(failureInfos => (
        <div className={failureDetails} key={failureInfos.failureName}>
          <div className={faultContainer}>
            <div className={faultInfo}>
              <p className={title}>{t("project.fault_event.side_panel.details.fault_name")}</p>
              <p className={value}>{failureInfos.name}</p>
            </div>
            {failureInfos.isPromoted && (
              <TooltipV2
                className={faultTooltip}
                overlay={t("project.fault_event.side_panel.details.tooltip.fault_filter_type")}
              >
                <div>
                  <Tag className={faultBadge}>
                    <StarLine size="s" />
                    <span>{t("project.fault_event.side_panel.details.fault_type")}</span>
                  </Tag>
                </div>
              </TooltipV2>
            )}
          </div>

          <p className={title}>{t("project.fault_event.side_panel.details.description")}</p>
          {failureInfos.description && <p className={value}>{failureInfos.description}</p>}

          <p className={title}>{t("project.fault_event.side_panel.details.degradation_type")}</p>
          {failureInfos.degradationType && <p className={value}>{failureInfos.degradationType}</p>}

          <p className={title}>{t("project.fault_event.side_panel.details.category")}</p>
          {failureInfos.category && <p className={value}>{failureInfos.category}</p>}

          <p className={title}>{t("project.fault_event.side_panel.details.system")}</p>
          {failureInfos.system && <p className={value}>{failureInfos.system}</p>}

          <p className={title}>{t("project.fault_event.side_panel.details.equipment")}</p>
          {failureInfos.equipment.length > 0 && (
            <p className={value}>{failureInfos.equipment.join(", ")}</p>
          )}

          <p className={title}>{t("project.fault_event.side_panel.details.suspected_elements")}</p>
          {failureInfos.suspectedElements.length > 0 && (
            <p className={value}>{failureInfos.suspectedElements.join(", ")}</p>
          )}

          <p className={title}>{t("project.fault_event.side_panel.details.equipment_type")}</p>
          {failureInfos.equipmentType && <p className={value}>{failureInfos.equipmentType}</p>}
        </div>
      ))}
    </div>
  );
};
export default SidePanelDetails;

//#region CSS
const sidePanelDetailsContainer = css`
  width: 100%;
  padding: 16px;
`;
const failureDetails = css`
  & + & {
    border-top: 1px solid ${color.titanium200};
    margin-top: ${spacings.m};
    padding-top: ${spacings.m};
  }
`;
const value = css`
  margin-top: ${spacings.xxs};
  word-break: break-word;
`;
const title = css`
  white-space: nowrap;
  color: ${color.carbon700};

  &:not(:first-child) {
    margin-top: ${spacings.m};
  }

  &:not(:has(+ .${value})) {
    display: none;
  }
`;
const faultTooltip = css`
  display: flex;
  width: 268px;
  max-width: 350px;
  padding: ${spacings.m};
  flex-direction: column;
  align-items: flex-start;
  gap: ${spacings.xs};
  align-self: stretch;
`;

const faultContainer = css`
  display: flex;
  align-items: flex-start;
  gap: ${spacings.s};
  align-self: stretch;
`;

const faultInfo = css`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${spacings.xxs};
`;

const faultBadge = css`
  display: flex;
  align-items: center;
  gap: ${spacings.xxs};
  padding: ${spacings.xxs} ${spacings.xs};
  background: ${color.titanium100};
  border-radius: ${spacings.xxs};
  color: ${color.carbon800};
  font: ${fonts.body2};
  cursor: default;
`;
//#endregion CSS

//#region Functions
export const initializeFailureInfos = (
  faultEvent: FaultEvent,
  setup: Setup | null,
): FailureInfos[] => {
  if (!setup) return [];

  const failuresByName = new Map(setup.failures.map(failure => [failure.name, failure]));
  const faultNames = faultEvent.metadata.faults.map(fault => fault.name);

  return faultNames.flatMap(faultName => {
    const diagnosisFailure = failuresByName.get(faultName);

    if (!diagnosisFailure) return [];

    return [
      {
        failureName: diagnosisFailure.name,
        name: diagnosisFailure.displayName,
        description: diagnosisFailure.description,
        degradationType: diagnosisFailure.degradationTypeId,
        category: diagnosisFailure.failureCategoryId,
        system: diagnosisFailure.systemId,
        equipment: diagnosisFailure.equipments
          .map(equipment => equipment.equipment.name)
          .sort(stringComparator),
        suspectedElements: diagnosisFailure.suspectedElements
          .map(element => element.suspectedElement.name)
          .sort(stringComparator),
        equipmentType: diagnosisFailure.equipmentTypeId,
        isPromoted: diagnosisFailure.filters.some(filter => filter.rule === "PROMOTION"),
      },
    ];
  });
};
//#endregion Functions
